import { useEffect, useState } from 'react';
import { Layout, Row, Col, Typography, Card, Space, message, Flex, Alert, Empty, Spin } from 'antd';
import { WhatsAppOutlined, CopyOutlined, WarningOutlined } from '@ant-design/icons';
import { PAY_METHODS } from '../../../constants/AppConstants';
import { callGetActivePayMethods } from "../../../services/paymentService";
import { callGetPhones } from '../../../services/phonesService';
import { showNotificationTopEnd, maskCard, formatPhoneNumber } from "../../../util/utils";

import logoBBVA from "../../../assets/images/bancos/logo_bbva_icon.svg";
import logoSantander from "../../../assets/images/bancos/logo_santander_icon.svg";
import logoBanamex from "../../../assets/images/bancos/logo_banamex_icon.png";
import logoScotiabank from "../../../assets/images/bancos/logo_scotiabank_icon.svg";
import logoBancoppel from "../../../assets/images/bancos/logo_bancoppel_icon.svg";
import logoBanorte from "../../../assets/images/bancos/logo_banorte_icon.png";
import logoBancoAzteca from "../../../assets/images/bancos/logo_banco_azteca_icon.png";

import './Accounts.css';

const { Title, Text, Link } = Typography;
const { Content } = Layout;

const logos = {
    bbva: logoBBVA,
    santander: logoSantander,
    banamex: logoBanamex,
    scotiabank: logoScotiabank,
    bancoppel: logoBancoppel,
    bancoazteca: logoBancoAzteca,
    banorte: logoBanorte,
};

export default function Accounts() {
    const [messageApi, contextHolder] = message.useMessage();
    const [data, setData] = useState([]);
    const [phones, setPhones] = useState([]);
    const [loadingPhones, setLoadingPhones] = useState(false);
    const [loadingPayMethods, setLoadingPayMethods] = useState(false);
    const [phonesError, setPhonesError] = useState("");
    const [payMethodsError, setPayMethodsError] = useState("");


    useEffect(() => {
        getPhones();
        getPaymentMethods();
        // eslint-disable-next-line
    }, []);


    const copyToClipboard = (text, messageApi) => {
        navigator.clipboard.writeText(text);
        messageApi.success('¡Copiado al portapapeles!');
    };


    const getPhones = async () => {
        setLoadingPhones(true);
        setPhonesError("");
        const response = await callGetPhones();
        if (response?.status === 200) {
            setPhones(response.data || []);
        } else {
            setPhones([]);
            setPhonesError("No se pudieron cargar los contactos de WhatsApp.");
            showNotificationTopEnd("error", "Error, por favor recarga la pagina");
        }
        setLoadingPhones(false);
    };


    const getPaymentMethods = async () => {
        setLoadingPayMethods(true);
        setPayMethodsError("");
        const response = await callGetActivePayMethods();
        if (response?.status === 200) {
            setData(response.data || []);
        } else {
            setData([]);
            setPayMethodsError("No se pudieron cargar los metodos de pago.");
            showNotificationTopEnd("error", "Error, por favor recarga la pagina");
        }
        setLoadingPayMethods(false);
    };


    return (
        <Layout>
            <Flex className="accounts-section-container">
                <Title className="accounts-section-title" strong>
                    {PAY_METHODS.TITLE}
                </Title>
            </Flex>
            <Content className="payment-methods-container">
                {contextHolder}
                <div style={{ textAlign: "center" }}>
                    <Text className="description-text">
                        <WhatsAppOutlined /> {PAY_METHODS.DESCRIPTION}
                    </Text>
                </div>
                <br />
                <Row gutter={[24, 24]} justify="center" style={{ marginBottom: 40 }}>
                    {loadingPhones ? (
                        <Col span={24}>
                            <Flex justify="center">
                                <Spin size="large" />
                            </Flex>
                        </Col>
                    ) : phonesError ? (
                        <Col span={24}>
                            <Alert type="error" showIcon message={phonesError} />
                        </Col>
                    ) : Array.isArray(phones) && phones.length > 0 ? phones.map((contact, index) => (
                        <Col xs={24} sm={12} md={8} key={index}>
                            <Link
                                href={`https://api.whatsapp.com/send?phone=${contact.lada}${contact.phoneNumber}`}
                                target="_blank"
                            >
                                <Card className="contact-card">
                                    <WhatsAppOutlined />
                                    <Text className="phone-number">
                                        +{contact.lada} {formatPhoneNumber(contact.phoneNumber)}
                                    </Text>
                                    <Text className="team-label">
                                        {/* {contact.main ? "Principal" : "Secundario"} */}
                                        {PAY_METHODS.LABEL}
                                    </Text>
                                </Card>
                            </Link>
                        </Col>
                    )) : (
                        <Col span={24}>
                            <Empty description="No hay contactos disponibles por el momento." />
                        </Col>
                    )}
                </Row>
                <div className="warning-section">
                    <Title level={5} className="warning-title">
                        <WarningOutlined /> {PAY_METHODS.WARNING.TITLE}
                    </Title>
                    <ul className="warning-list">
                        {PAY_METHODS.WARNING.RULES.map((point, index) => (
                            <li key={index}>
                                <Text>
                                    {point}
                                </Text>
                            </li>
                        ))}
                    </ul>
                </div>
                <Row gutter={[24, 24]} justify="center">
                    {loadingPayMethods ? (
                        <Col span={24}>
                            <Flex justify="center">
                                <Spin size="large" />
                            </Flex>
                        </Col>
                    ) : payMethodsError ? (
                        <Col span={24}>
                            <Alert type="error" showIcon message={payMethodsError} />
                        </Col>
                    ) : Array.isArray(data) && data.length > 0 ? data.map((bank, index) => (
                        <Col xs={24} sm={24} md={12} lg={10} key={index}>
                            <Card className="bank-card">
                                {<img src={logos[bank.bank.toLowerCase().replace(/\s/g, "")]} alt={bank.name} className="bank-logo-overlay" />}

                                <Title level={4} className="bank-name">{bank.bank}</Title>

                                <div className="bank-detail-row">
                                    <Text className="bank-detail-label">Nombre:</Text>
                                    <Text className="bank-detail-value">{bank.owner}</Text>
                                </div>
                                <div className="bank-detail-row">
                                    <Text className="bank-detail-label">Número Tarjeta:</Text>
                                    <Space>
                                        <Text className="bank-detail-value">{maskCard(bank.cardNumber)}</Text>
                                        <CopyOutlined
                                            className="copy-icon"
                                            onClick={() => copyToClipboard(bank.cardNumber, messageApi)}
                                        />
                                    </Space>
                                </div>
                            </Card>
                        </Col>
                    )) : (
                        <Col span={24}>
                            <Empty description="No hay metodos de pago disponibles por el momento." />
                        </Col>
                    )}
                </Row>
            </Content>
        </Layout >
    );
};